import { Field, Form, Formik, FormikErrors } from 'formik';
import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { Environment } from 'src/Environment';
import {
  isValidName,
  isValidEmail,
  isValidMobileNumber,
  isValidID,
  validateEmailTradeRoot,
} from 'src/helpers/validation';
import { FloatingInputVersionTwo, FormRowLayout, FormLayout } from 'src/components';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import {
  phoneNumberMaskSpaced,
  idMask,
  stripWhitespace,
  spaceBy4Mask,
  passportMask,
  asylumMask,
  refugeeMask,
  passportRegex,
  refugeeRegex,
  asylumRegex,
} from 'src/helpers/formatting';
import { SOV_DETAILS } from 'src/helpers/constants';
import { LockedFieldNames, SalesforceContext, Steps } from 'src/context/salesforce-context';
import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP } from 'src/helpers/constants';
import { CONSUMER_TYPE } from 'src/services/salesforce.service';
import { StyledDatePicker } from 'src/components/styled-date-picker/styled-date-picker';
import {
  IdentityTypeDirectors,
  ListItem,
  MARITAL_STATUSES,
  isUnderAge,
  isUserUnderAgeIdNumber,
  isValidPassportExpiryDate,
} from 'src/features/common';
import { CREDIT_VETTING_TITLES, MIN_DATE_ALLOWED } from 'src/common';
import UnderageModal from '../underage-modal/underage-modal';
import { getFormStatus, merger } from 'src/features/onboarding/helpers/fields-status';
import mergeWith from 'lodash.mergewith';
import { useKillApplication } from 'src/hooks/use-kill-application';
import { COUNTRY_CODES } from 'src/services/salesforce-service-helpers/country-codes';
import { IDENTITY_TYPE_LIST } from 'src/features/onboarding/pages/personal-details/personal-details';
import './your-details.scss';
import { isRented } from 'src/features/common/helpers/get-rented-terminal';

const GENDER_LIST = ['Female', 'Male'];

export default function YourDetails(props) {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const { merchant, setMerchant, customerType, lead, isFieldLocked, isVodacomBusinessChannel } =
    useContext(SalesforceContext);
  const [isUserUnderAge, setUserIsUnderAge] = useState(false);
  const { setApplicationKilled } = useKillApplication(Steps.YOUR_DETAILS, Steps.YOUR_DETAILS);

  const isCreditVettingEnabled =
    !Environment.current?.disabledFeatures?.creditVetting && isRented(merchant);
  const isPassportEnabled = !Environment.current?.disabledFeatures?.passport && isRented(merchant);

  const outsideRef = useRef(null);
  const [errorState, setErrorState] = useState(false);

  const isSovRequest = merchant?.account?.sov_requested;

  useEffect(() => {
    if (merchant?.account?.business_type) {
      const assistantId = lead.dealerId || lead.agentId || '';
      TealiumEvents.aboutYouPageLoadFormStartCombined(
        merchant.account.business_type,
        customerType,
        assistantId,
        merchant.isPendingApp,
      );
    }
  }, [merchant?.account?.business_type]);

  useEffect(() => {
    props.toggleNext(isCompletedForm(getContactDetails()));
  }, [merchant, errorState]);

  const textTable = {
    dealer: {
      title: 'Tell us about the customer',
    },
    agent: {
      title: 'Tell us about the customer',
    },
    normal: {
      title:
        lead.consumerType === CONSUMER_TYPE.owner
          ? 'Tell us about yourself'
          : 'Tell us about the customer',
    },
    pos: {
      title: 'Tell us about yourself',
    },
  };

  const FIRST_NAME_MAX_LENGTH = 40;

  const getIdPlaceholder = (idType) => {
    switch (idType) {
      case IdentityTypeDirectors.RSA_ID:
        return 'RSA ID Number';
      case IdentityTypeDirectors.PASSPORT:
        return 'Passport number';
      case IdentityTypeDirectors.REFUGEE:
      case IdentityTypeDirectors.ASYLUM_SEEKER:
      default:
        return 'Document ID';
    }
  };

  const setContactDetails = (newMerchantDetails) => {
    setMerchant({
      ...merchant,
      contact: {
        ...merchant.contact,
        surname: newMerchantDetails.surname,
        phone: newMerchantDetails.mobileNumber,
        id_type: newMerchantDetails.idType.value,
        id_number: newMerchantDetails.idNumber,
        first_names: newMerchantDetails.firstNames,
        email: newMerchantDetails.email,
        Occupation: newMerchantDetails.occupation,
        ...(isCreditVettingEnabled && {
          title: newMerchantDetails.title,
          initials: newMerchantDetails.initials,
          marital_status: newMerchantDetails.maritalStatus,
        }),
        date_of_birth: newMerchantDetails.dateOfBirth || null,
        gender: newMerchantDetails.gender || null,
        passport_expiry_date: newMerchantDetails.expiryDate || null,
        country_of_origin: newMerchantDetails.countryOfOrigin || null,
      },
    });
  };

  const getContactDetails = () => ({
    firstNames: merchant?.contact?.first_names || '',
    surname:
      merchant?.contact?.surname !== 'Last Name Placeholder'
        ? merchant?.contact?.surname || ''
        : '',
    idType: IDENTITY_TYPE_LIST.find(
      (type) => type.value === (merchant?.contact?.id_type || IdentityTypeDirectors.RSA_ID),
    ),
    idNumber: merchant?.contact?.id_number || '',
    title: merchant?.contact?.title || '',
    initials: merchant?.contact?.initials || '',
    dateOfBirth: merchant?.contact?.date_of_birth || null,
    gender: merchant?.contact?.gender || '',
    expiryDate: merchant?.contact?.passport_expiry_date || null,
    countryOfOrigin: merchant?.contact?.country_of_origin || '',
    email: merchant?.contact?.email || '',
    mobileNumber: merchant?.contact?.phone || '',
    occupation: merchant?.contact?.Occupation || '',
    maritalStatus: merchant?.contact?.marital_status || '',
  });

  const initialStatus = useMemo(() => {
    const defaultStatus = {
      initialDisabled: {
        firstNames: isFieldLocked(LockedFieldNames.CONTACT_FIRST_NAMES),
        email: isFieldLocked(LockedFieldNames.CONTACT_EMAIL),
        mobileNumber: isFieldLocked(LockedFieldNames.CONTACT_PHONE),
        idNumber: isFieldLocked(LockedFieldNames.CONTACT_ID_NUMBER),
        surname: false,
        idType: false,
        title: false,
        initials: false,
        dateOfBirth: false,
        gender: false,
        expiryDate: false,
        countryOfOrigin: false,
        occupation: false,
      },
    };

    if (merchant.isPendingApp && !merchant?.pageUpdates) {
      throw new Error('Your details: in pending app but no page updates available');
    }

    if (!merchant.isPendingApp) {
      return defaultStatus;
    }

    const allFields = getContactDetails();
    const pendingAppStatus = getFormStatus(merchant.pageUpdates, allFields, Steps.YOUR_DETAILS);

    if (isVodacomBusinessChannel) {
      return mergeWith(pendingAppStatus, defaultStatus, merger);
    }

    return pendingAppStatus;
  }, [merchant?.isPendingApp, merchant?.pageUpdates]);

  const getIdMask = (values) => {
    switch (values.idType?.value) {
      case IdentityTypeDirectors.RSA_ID:
        return idMask;
      case IdentityTypeDirectors.PASSPORT:
        return passportMask;
      case IdentityTypeDirectors.ASYLUM_SEEKER:
        return asylumMask;
      case IdentityTypeDirectors.REFUGEE:
        return refugeeMask;
      default:
        return spaceBy4Mask;
    }
  };

  return (
    <div ref={outsideRef}>
      <div className="YourDetails" data-testid="YourDetails">
        <Formik<FormFields>
          initialValues={getContactDetails()}
          initialStatus={initialStatus}
          validate={validateForm}
          validateOnChange={true}
          validateOnBlur={false}
          enableReinitialize={true}
          onSubmit={() => {}}
        >
          {({ values }) => {
            const yourPersonalDetails = () => (
              <>
                <p className="page-title content-normal has-padding-bottom-2 has-mobile-padding-bottom-3 has-text-centered">
                  The authorised business representative must complete the application as a selfie
                  photo will be required later in the process
                </p>
                <Form>
                  <UnderageModal
                    show={isUserUnderAge}
                    onExitApplication={() => {
                      setApplicationKilled(true);
                    }}
                    onClose={() => setUserIsUnderAge(false)}
                  />
                  {isCreditVettingEnabled && (
                    <FormRowLayout>
                      {[
                        () => (
                          <Field
                            name="title"
                            type="text"
                            placeholder="Title"
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInputVersionTwo}
                            fieldType="select"
                            items={CREDIT_VETTING_TITLES}
                            getItemDisplayValue={(item) => item}
                            className="dark top-field"
                            automationid={`${props.automationPrefix}_Field_Title`}
                            autoFocus
                          />
                        ),
                        () => (
                          <Field
                            name="initials"
                            type="text"
                            placeholder="Initials"
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInputVersionTwo}
                            fieldType="standard"
                            className="dark top-field"
                            automationid={`${props.automationPrefix}_Field_Initials`}
                          />
                        ),
                      ]}
                    </FormRowLayout>
                  )}

                  <FormLayout>
                    <Field
                      name="firstNames"
                      type="text"
                      placeholder="First name"
                      maxLength={FIRST_NAME_MAX_LENGTH}
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInputVersionTwo}
                      fieldType="standard"
                      className="dark top-field"
                      automationid="Onboarding_Create_Account_Your_Details_Field_First_Names"
                      autoFocus={!isCreditVettingEnabled}
                    />
                    <Field
                      name="surname"
                      type="text"
                      placeholder="Surname"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInputVersionTwo}
                      fieldType="standard"
                      className="dark"
                      automationid="Onboarding_Create_Account_Your_Details_Field_Surname"
                    />
                    <Field
                      name="email"
                      type="text"
                      placeholder="Email address"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInputVersionTwo}
                      fieldType="standard"
                      className={`dark ${values.idType?.value ? '' : 'top-field'}`}
                      automationid="Onboarding_Create_Account_Your_Details_Field_Email"
                    />
                    <Field
                      name="mobileNumber"
                      mask={phoneNumberMaskSpaced}
                      id="mobileNumber"
                      automationid="Onboarding_Create_Account_Your_Details_Field_Mobile_Number"
                      placeholder="Mobile number"
                      className="dark bottom-field"
                      type="tel"
                      guide={!inMiniProgram}
                      fieldType="masked"
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInputVersionTwo}
                    />
                    {isSovRequest &&
                      lead.typeOfBusiness === BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop && (
                        <Field
                          name="occupation"
                          type="text"
                          placeholder="Occupation"
                          items={SOV_DETAILS.Occupations}
                          getItemDisplayValue={(item) => item}
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInputVersionTwo}
                          fieldType="select"
                          isDark={true}
                          automationIdPrefix="Onboarding_Create_Account_Your_Details_Occupation"
                        />
                      )}
                    {isPassportEnabled && !isCreditVettingEnabled && (
                      <Field
                        name="idType"
                        type="text"
                        placeholder="Identity Type"
                        fieldType="select"
                        items={IDENTITY_TYPE_LIST}
                        getItemDisplayValue={(item) => item.label}
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInputVersionTwo}
                        automationid="Revamp_Personal_Details_Consumer_Type"
                        className="dark"
                      />
                    )}
                    <Field
                      name="idNumber"
                      className="dark"
                      type={values.idType?.value === IdentityTypeDirectors.RSA_ID ? 'tel' : 'text'}
                      placeholder={getIdPlaceholder(values.idType?.value)}
                      component={MiniAppFocusWrapper}
                      innerComponent={FloatingInputVersionTwo}
                      fieldType="masked"
                      mask={getIdMask(values)}
                      guide={
                        !inMiniProgram && values.idType?.value === IdentityTypeDirectors.RSA_ID
                      }
                      automationid="Revamp_Personal_Details_RSA_ID_Number"
                    />

                    {values.idType?.value !== IdentityTypeDirectors.RSA_ID && (
                      <Field
                        name="countryOfOrigin"
                        type="text"
                        placeholder="Country of origin"
                        items={COUNTRY_CODES}
                        getItemDisplayValue={(item) => item}
                        component={MiniAppFocusWrapper}
                        innerComponent={FloatingInputVersionTwo}
                        fieldType="select"
                        isDark
                      />
                    )}

                    {isCreditVettingEnabled && (
                      <>
                        <Field
                          name="maritalStatus"
                          type="text"
                          placeholder="Marital status"
                          items={Object.values(MARITAL_STATUSES)}
                          getItemDisplayValue={(item: string) => item}
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInputVersionTwo}
                          fieldType="select"
                          isDark
                          automationid="Revamp_Personal_Details_Marital_Status"
                        />

                        {values.idType?.value !== IdentityTypeDirectors.RSA_ID && (
                          <Field
                            name="gender"
                            type="text"
                            placeholder="Gender"
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInputVersionTwo}
                            items={GENDER_LIST}
                            getItemDisplayValue={(item) => item}
                            fieldType="select"
                            className="dark"
                            automationid={`${props.automationPrefix}_Field_Date_Of_Birth`}
                          />
                        )}
                      </>
                    )}

                    {values.idType?.value !== IdentityTypeDirectors.RSA_ID && (
                      <FormRowLayout>
                        {[
                          () => (
                            <Field
                              name="expiryDate"
                              fieldName="Passport expiry date"
                              component={StyledDatePicker}
                            />
                          ),
                          () => (
                            <Field
                              name="dateOfBirth"
                              fieldName="Date of birth"
                              component={StyledDatePicker}
                              minDate={MIN_DATE_ALLOWED}
                              maxDate={new Date()}
                            />
                          ),
                        ]}
                      </FormRowLayout>
                    )}
                  </FormLayout>
                  <div className="mini-spacer" />
                </Form>
              </>
            );
            return (
              <>
                <div className="your-details-container">
                  <h1 className="page-title heading-four has-padding-bottom-3 has-mobile-padding-bottom-5 has-text-centered">
                    {textTable[customerType].title}
                  </h1>
                  {yourPersonalDetails()}
                </div>
              </>
            );
          }}
        </Formik>
        <div className="spacer" />
      </div>
    </div>
  );

  function isCompletedForm(values: FormFields) {
    const invalidFirstName = !values?.firstNames;
    const invalidSurname = !values?.surname;
    const invalidIdNumber = !values?.idNumber;
    const invalidEmail = !values?.email;
    const invalidMobileNumber = !values?.mobileNumber;
    const invalidOccupation = isSovRequest ? !values?.occupation : false;

    return !(
      errorState ||
      invalidFirstName ||
      invalidSurname ||
      invalidIdNumber ||
      invalidEmail ||
      invalidMobileNumber ||
      invalidOccupation
    );
  }

  function validateForm(values: FormFields) {
    //Strip whitespace from ID, passport, and mobile numbers
    values.idNumber = stripWhitespace(values.idNumber);
    values.mobileNumber = stripWhitespace(values.mobileNumber);

    const errors: FormikErrors<FormFields> = {};

    if (!isValidName(values.firstNames)) {
      errors.firstNames = 'The first name entered is invalid';
    }

    if (!isValidName(values.surname)) {
      errors.surname = 'The surname entered is invalid';
    }

    const emailTradeRootValidationError = validateEmailTradeRoot(values.email);

    if (emailTradeRootValidationError) {
      errors.email = emailTradeRootValidationError;
    } else if (!isValidEmail(values.email)) {
      errors.email = 'The email address entered is invalid';
    }

    if (!isValidMobileNumber(values.mobileNumber)) {
      errors.mobileNumber = 'The mobile number entered is invalid';
    }

    if (isCreditVettingEnabled) {
      if (!isValidName(values.initials)) {
        errors.initials = 'The initials entered is invalid';
      }

      if (!values?.title) {
        errors.title = 'The title entered is invalid';
      }
      if (!values?.maritalStatus) {
        errors.maritalStatus = 'The marital status entered is invalid';
      }
    }

    if (values.idType?.value === IdentityTypeDirectors.RSA_ID) {
      if (
        values.idNumber &&
        isValidID(stripWhitespace(values.idNumber)) &&
        isUserUnderAgeIdNumber(values.idNumber)
      ) {
        errors.idNumber = 'The age limit is 18 years or older';
        setUserIsUnderAge(true);
      }

      if (!values.idNumber || !isValidID(stripWhitespace(values.idNumber))) {
        errors.idNumber = 'The ID number entered is invalid';
      }
    }

    if (
      values.idType?.value === IdentityTypeDirectors.PASSPORT &&
      values.idNumber &&
      !passportRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Passport number entered is invalid';
    }

    if (
      values.idType?.value === IdentityTypeDirectors.REFUGEE &&
      values.idNumber &&
      !refugeeRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Document ID entered is invalid';
    }

    if (
      values.idType?.value === IdentityTypeDirectors.ASYLUM_SEEKER &&
      values.idNumber &&
      !asylumRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Document ID entered is invalid';
    }

    if (values.idType?.value !== IdentityTypeDirectors.RSA_ID) {
      if (!values.countryOfOrigin) {
        errors.countryOfOrigin = 'Country of origin is invalid';
      }

      if (!values.dateOfBirth) {
        errors.dateOfBirth = 'Date of birth is invalid';
      }

      if (values.dateOfBirth && isUnderAge(new Date(values.dateOfBirth))) {
        errors.dateOfBirth = 'The age limit is 18 years or older';
        setUserIsUnderAge(true);
      }

      if (!values.expiryDate) {
        errors.expiryDate = `${
          values.idType?.value === IdentityTypeDirectors.PASSPORT ? 'Passport' : 'Document'
        } expiry date is invalid`;
      }

      if (values.expiryDate && !isValidPassportExpiryDate(new Date(values.expiryDate))) {
        errors.expiryDate = `Your ${
          values.idType?.value === IdentityTypeDirectors.PASSPORT ? 'passport' : 'document'
        } must be valid for a minimum of 6 months.`;
      }
    }

    const invalidOccupation = isSovRequest ? !values.occupation : false;
    if (invalidOccupation) {
      errors.occupation = 'The occupation entered is invalid';
    }

    if (
      isCreditVettingEnabled &&
      values.idType?.value !== IdentityTypeDirectors.RSA_ID &&
      !values.gender
    ) {
      errors.gender = 'The gender entered is invalid';
    }

    let anyErrors = false;
    Object.keys(errors).forEach((key) => {
      if (errors[key] !== null) {
        anyErrors = true;
      }
    });
    setErrorState(anyErrors);

    if (!anyErrors) {
      setContactDetails(values);
    }
    return errors;
  }
}

type FormFields = {
  title?: string;
  initials?: string;
  firstNames?: string;
  surname?: string;
  email?: string;
  mobileNumber?: string;
  occupation?: string;
  foreignNationalType?: string;
  idNumber?: string;
  idType?: ListItem<IdentityTypeDirectors>;
  countryOfOrigin: string;
  gender?: string;
  expiryDate: string;
  dateOfBirth?: string;
  maritalStatus?: string;
};
